/**
 * Baustein-Katalog des Leitstands.
 *
 * Zeigt alle Bausteine der Verwaltungsebene mit Beispielwerten — ohne
 * Datenbank, ohne API. Zweck: Beim Umbau der rund fünfzig Verwaltungsseiten
 * nachsehen können, was es gibt und wie es aussieht, statt es in einer
 * echten Seite zu suchen.
 *
 * Die Zahlen stammen aus dem Entwurf vom 13.08.2026 und sind Beispiele,
 * keine Betriebsdaten.
 */

import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import {
  Anteilsliste,
  Karte,
  Kennung,
  Kennzahl,
  Kopfzelle,
  Marke,
  Notiz,
  Rangliste,
  Tabelle,
  Zahl,
  Zeitbalken,
  Zelle,
  Zustand,
} from '../components/leitstand';

function Abschnitt({
  titel,
  hinweis,
  children,
}: {
  titel: string;
  hinweis: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-[17px] font-semibold">{titel}</h2>
        <p className="text-[13px] text-tinte-3">{hinweis}</p>
      </div>
      {children}
    </section>
  );
}

export default function LeitstandBausteine() {
  return (
    <LeitstandRahmen titel="Baustein-Katalog" benutzer="Richard Scherbaum">
      <div className="flex flex-col gap-10">
        <Notiz titel="Wozu diese Seite">
          Alle Bausteine der Verwaltungsebene an einer Stelle, mit Beispielwerten. Sie zeigt keine
          Betriebsdaten und ist keine Auswertung — sie ist die Werkbank für den Umbau.
        </Notiz>

        <Abschnitt
          titel="Kennzahlen"
          hinweis="Eine große Zahl mit Einordnung darunter. Die Nebenzeile färbt sich nach betrieblicher Bewertung, nicht nach Richtung: mehr Störzeit ist „runter“."
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3.5">
            <Kennzahl
              marke="Gewaschen"
              wert="98,96"
              einheit="t"
              neben="+4,2 % zur Vorwoche"
              tendenz="hoch"
            />
            <Kennzahl marke="Takte" wert="2 314" neben="42,8 kg je Takt" />
            <Kennzahl
              marke="Nutzungsgrad"
              wert="82,1"
              einheit="%"
              neben="Laufzeit an der Gesamtzeit"
            />
            <Kennzahl
              marke="Störzeit"
              wert="6,0"
              einheit="%"
              neben="4,1 h in der Woche"
              tendenz="runter"
            />
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3.5">
            <Kennzahl
              marke="Datenerfassung"
              wert="läuft"
              neben="letzter Kontakt vor 8 s"
              klein
              tendenz="hoch"
            />
            <Kennzahl
              marke="Fernzugriff"
              wert="verbunden"
              neben="100.68.169.25"
              klein
              tendenz="hoch"
            />
            <Kennzahl
              marke="Datenträger"
              wert="2"
              einheit="% Verschleiß"
              neben="21 348 GB geschrieben"
              klein
            />
          </div>
        </Abschnitt>

        <Abschnitt
          titel="Rangliste"
          hinweis="Was am meisten kostet, mit Balken zum Vergleichen. Der Zweitwert steht leiser darunter — hier die Häufigkeit, die eine ganz andere Rangfolge ergäbe."
        >
          <Karte titel="Warum stand die Anlage" marke="50 kg · 90 Tage · nach Ausfallzeit">
            <Rangliste
              eintraege={[
                {
                  nr: '445',
                  text: 'Hubband oder Trocknersteuerung nicht frei',
                  wert: '89,4 h',
                  anteil: 100,
                  neben: '4 083 ×',
                },
                {
                  nr: '020',
                  text: 'Notaus-Kette Presse / Zentrifuge nicht eingeschaltet',
                  wert: '86,1 h',
                  anteil: 96,
                  neben: '892 ×',
                },
                {
                  nr: '501',
                  text: 'Presse / Zentrifuge — Kommunikation gestört',
                  wert: '37,8 h',
                  anteil: 42,
                  neben: '472 ×',
                },
                {
                  nr: '412',
                  text: 'Presse / Zentrifuge ist nicht frei',
                  wert: '32,2 h',
                  anteil: 36,
                  neben: '5 199 ×',
                },
                {
                  nr: '416',
                  text: 'Kein Beladeposten über Trichter',
                  wert: '14,5 h',
                  anteil: 16,
                  neben: '35 092 ×',
                },
              ]}
            />
          </Karte>
        </Abschnitt>

        <Abschnitt
          titel="Zeitbalken"
          hinweis="Woran die Zeit ging, in einem Streifen. Schmale Abschnitte tragen keine Beschriftung — sie stünde über dem Rand."
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(330px,1fr))] gap-5">
            <Karte titel="Zeitaufteilung" marke="50 kg">
              <Zeitbalken
                abschnitte={[
                  { anteil: 82.1, farbe: 'lauf', name: 'Laufzeit', wert: '6,9 h' },
                  { anteil: 6.0, farbe: 'stoer', name: 'Störzeit', wert: '0,5 h' },
                  { anteil: 11.9, farbe: 'stand', name: 'Standzeit', wert: '1,0 h' },
                ]}
              />
            </Karte>
            <Karte titel="Zeitaufteilung" marke="25 kg">
              <Zeitbalken
                abschnitte={[
                  { anteil: 68.8, farbe: 'lauf', name: 'Laufzeit', wert: '6,4 h' },
                  { anteil: 15.1, farbe: 'stoer', name: 'Störzeit', wert: '1,4 h' },
                  { anteil: 16.1, farbe: 'stand', name: 'Standzeit', wert: '1,5 h' },
                ]}
              />
            </Karte>
          </div>
        </Abschnitt>

        <Abschnitt
          titel="Anteilslisten"
          hinweis="Für Verbräuche und Verteilungen. Die Spur zeigt den Anteil am Spitzenreiter."
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(330px,1fr))] gap-5">
            <Karte titel="Waschmittel" marke="Spätschicht 12.08.">
              <Anteilsliste
                eintraege={[
                  { name: 'Silex emulgierend K1', wert: '24 466', anteil: 100 },
                  { name: 'Oxybrite K5', wert: '13 839', anteil: 57 },
                  { name: 'Ozonit super K5', wert: '12 862', anteil: 53 },
                  { name: 'Finale liquid K14', wert: '10 947', anteil: 45 },
                ]}
              />
            </Karte>
            <Karte titel="Programme" marke="Anteil am Gewicht">
              <Anteilsliste
                eintraege={[
                  { name: 'Programm 40', wert: '31,2 %', anteil: 100 },
                  { name: 'Programm 2', wert: '18,7 %', anteil: 60 },
                  { name: 'Programm 11', wert: '14,3 %', anteil: 46 },
                  { name: 'Programm 66', wert: '11,8 %', anteil: 38 },
                ]}
              />
            </Karte>
          </div>
        </Abschnitt>

        <Abschnitt
          titel="Tabelle mit Zustandsspalte"
          hinweis="Alle Zahlen rechtsbündig und in Festbreite, damit man Spalten liest. Die Zustandsspalte sagt, was sonst erst beim Rechnen auffällt."
        >
          <Karte titel="Woche 10.–16.08.2026" marke="Alle Anlagen" tabelle>
            <Tabelle>
              <thead>
                <tr>
                  <Kopfzelle>Tag</Kopfzelle>
                  <Kopfzelle>Schicht</Kopfzelle>
                  <Kopfzelle>Anlage</Kopfzelle>
                  <Kopfzelle rechts>Gewicht</Kopfzelle>
                  <Kopfzelle rechts>Takte</Kopfzelle>
                  <Kopfzelle rechts>kg/Takt</Kopfzelle>
                  <Kopfzelle rechts>Störzeit</Kopfzelle>
                  <Kopfzelle rechts>Nutzung</Kopfzelle>
                  <Kopfzelle>Zustand</Kopfzelle>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Zelle>Do 13.08.</Zelle>
                  <Zelle>
                    <Kennung>FRÜH</Kennung>
                  </Zelle>
                  <Zelle>50 kg</Zelle>
                  <Zelle rechts zahl>
                    6 018 kg
                  </Zelle>
                  <Zelle rechts zahl>
                    140
                  </Zelle>
                  <Zelle rechts zahl>
                    43,0
                  </Zelle>
                  <Zelle rechts zahl>
                    0,5 h
                  </Zelle>
                  <Zelle rechts zahl>
                    82 %
                  </Zelle>
                  <Zelle>
                    <Zustand art="gut">normal</Zustand>
                  </Zelle>
                </tr>
                <tr>
                  <Zelle>Do 13.08.</Zelle>
                  <Zelle>
                    <Kennung>FRÜH</Kennung>
                  </Zelle>
                  <Zelle>25 kg</Zelle>
                  <Zelle rechts zahl>
                    2 981 kg
                  </Zelle>
                  <Zelle rechts zahl>
                    140
                  </Zelle>
                  <Zelle rechts zahl>
                    21,3
                  </Zelle>
                  <Zelle rechts zahl>
                    1,4 h
                  </Zelle>
                  <Zelle rechts zahl>
                    69 %
                  </Zelle>
                  <Zelle>
                    <Zustand art="warnung">Störzeit hoch</Zustand>
                  </Zelle>
                </tr>
                <tr>
                  <Zelle letzte>Mo 10.08.</Zelle>
                  <Zelle letzte>
                    <Kennung>FRÜH</Kennung>
                  </Zelle>
                  <Zelle letzte>50 kg</Zelle>
                  <Zelle letzte rechts zahl>
                    876 kg
                  </Zelle>
                  <Zelle letzte rechts zahl>
                    19
                  </Zelle>
                  <Zelle letzte rechts zahl>
                    46,1
                  </Zelle>
                  <Zelle letzte rechts zahl>
                    —
                  </Zelle>
                  <Zelle letzte rechts zahl>
                    —
                  </Zelle>
                  <Zelle letzte>
                    <Zustand art="stoerung">auffällig kurz</Zustand>
                  </Zelle>
                </tr>
              </tbody>
            </Tabelle>
          </Karte>
        </Abschnitt>

        <Abschnitt titel="Kleinteile" hinweis="Marke, Zahl in Festbreite, Kennung, Zustände.">
          <Karte titel="Übersicht">
            <div className="flex flex-wrap items-center gap-6">
              <Marke>Marke</Marke>
              <Zahl className="text-[17px]">1 234,56</Zahl>
              <Kennung>SPÄT</Kennung>
              <Zustand art="gut">normal</Zustand>
              <Zustand art="warnung">nicht gemessen</Zustand>
              <Zustand art="stoerung">ausgefallen</Zustand>
            </div>
          </Karte>
        </Abschnitt>

        <p className="border-t border-linie pt-4 text-[12.5px] text-tinte-3">
          Baustein-Katalog · Beispielwerte aus dem Entwurf vom 13.08.2026 · gilt nur für die
          Verwaltungsebene — die Erfassung an den Stationen bleibt unverändert
        </p>
      </div>
    </LeitstandRahmen>
  );
}
